import { api } from 'common';
import { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Typography } from '@vivaahealth/design-system';

import * as store from '../../reducers/store';
import NavBar, { View } from '../../components/NavBar';

import { PatientRouteParams } from '../router';
import { useForm } from '../../hooks/form/useForm';
import { withFetchPatient } from '../../components/hoc/withFetchPatient';
import { ReviewCards } from './ReviewCard';

import { useAsyncFlow } from '../../hooks/common/useAsyncFlow';
import { getSanitizedErrorMessage } from '../../utils/error';
import { validateFields } from '../../utils/form/field';
import { getReviewSectionEditPath } from '../../utils/form';
import {
  maybeForceCompleteInsuranceRelevantFields,
  REVIEW_SECTIONS,
  SectionType,
} from '../../utils/review';

import appStyles from '../App.module.css';
import spacingStyles from '../../styles/spacing.module.css';
import formStyles from '../FormStart/index.module.css';

interface Props {
  fields: store.FormFieldWithConfig[];
  onEdit: (sectionType: SectionType) => void;
  onContinue?: () => Promise<{ success: boolean }> | void;
  pending?: boolean;
  error?: string;
}

export const Review = ({
  fields,
  onEdit,
  onContinue,
  pending,
  error,
}: Props) => {
  const { t } = useTranslation('review');
  return (
    <div className={formStyles.PageWrapper}>
      {error && (
        <Alert variant="danger" className={'mb-4'}>
          {error}
        </Alert>
      )}
      <div className={spacingStyles.MbLarge}>
        <Typography variant="headline-small" bold>
          {t('reviewPageIntro')}
        </Typography>
      </div>
      <div className={spacingStyles.MbXLarge}>
        <ReviewCards
          fields={fields}
          onEdit={onEdit}
          sectionConfiguration={REVIEW_SECTIONS}
        />
      </div>
      {onContinue && (
        <Button onClick={onContinue} fullWidth disabled={pending}>
          {t('continue')}
        </Button>
      )}
    </div>
  );
};

const ReviewContainer = () => {
  const history = useHistory();
  const { authenticationCode, formId, formSetId } =
    useParams<PatientRouteParams>();
  if (!formId || !formSetId) throw new Error('No resource id given');

  const {
    form: reviewFormSource,
    startForm: _startForm,
    completeForm,
    setFieldValue,
    saveFieldValue,
    multiFormSet,
    formSet,
    patient,
    visibleFormFields,
  } = useForm({
    formId,
    authenticationCode,
  });

  const {
    execute: onContinue,
    pending: onContinuePending,
    error: onContinueError,
  } = useAsyncFlow(async () => {
    const updatedFields = await maybeForceCompleteInsuranceRelevantFields({
      fields: visibleFormFields,
      setFieldValue,
      saveFieldValue,
    });

    /**
     * We need it here for now because user could update a valid value to an invalid value in edit page, then clicks
     * `back` button and goes back to the review page. We need an extra catch here to prevent user from moving forward in the case.
     *
     * TODO: we won't need this if we move away from saving values on primitive change, the changes should be discarded unless a
     * `save` button is clicked on the page. Then user won't be able to move forward (/back to review page) with invalid values.
     * This is what design wants too and a soon follow up.
     */
    await validateFields({
      formSetId,
      fields: updatedFields,
      formFields: reviewFormSource.fields,
      practicePreference: formSet.practicePreference,
      patient,
    });
    await completeForm();
  });

  const {
    execute: startForm,
    pending: startFormPending,
    error: startFormError,
  } = useAsyncFlow(_startForm);

  const error = startFormError || onContinueError;
  const pending = startFormPending || onContinuePending;

  useEffect(() => {
    if (
      reviewFormSource.status === api.PrevisitForm.Status.Created &&
      !startFormPending
    ) {
      startForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFormSource.status, startFormPending]);

  useEffect(() => {
    if (!error) return;
    window.scrollTo(0, 0);
  }, [error]);

  if (!reviewFormSource || !reviewFormSource.isReviewable)
    throw new Error('Unknown review source');
  return (
    <div className={appStyles.App}>
      <NavBar view={View.Review} />
      <div className={formStyles.FormContainer}>
        <div className={formStyles.Form}>
          <Review
            fields={visibleFormFields}
            onContinue={onContinue}
            onEdit={(sectionType) => {
              history.push({
                pathname: getReviewSectionEditPath({
                  authenticationCode,
                  sectionType,
                  multi: multiFormSet,
                  formId,
                  formSetId,
                }),
                search: '',
              });
            }}
            pending={pending}
            error={getSanitizedErrorMessage(error)}
          />
        </div>
      </div>
    </div>
  );
};

export const ReviewPage = withFetchPatient(ReviewContainer);
export const ReviewPageMulti = withFetchPatient(ReviewContainer, {
  multi: true,
});
